<template>
    <section class="best-players-section">
        <PageHeader :title="title" :tag="headingTag">
            <SeasonSelect
                :model-value="selectedSeasonId"
                :seasons="availableSeasons"
                :multiple="false"
                :disabled="loading"
                @change="onSeasonChange"
            />
            <CompetitionStageSelect
                v-if="selectedSeasonId !== null"
                :competition-types="competitionTypes"
                :stages="stages"
                :disabled="loading"
                @change="onCompetitionChange"
            />
        </PageHeader>

        <div class="best-players-grid">
            <PlayerCategoryCard
                v-for="category in categories"
                :key="category.key"
                :category="category"
                :data="bestPlayersData[category.key]"
                :current-season-id="selectedSeasonId"
                :positions="positions"
                :selected-positions="selectedPositions[category.key] || positions.map(p => p.slug)"
                :selected-competitions="competitionsParam"
                :initial-sort="sortParams[category.key]"
                @sort-change="onSortChange"
                @positions-change="onPositionsChange"
                @data-updated="onCategoryDataUpdated"
            />
        </div>
    </section>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import axios from 'axios';
import PlayerCategoryCard from './PlayerCategoryCard.vue';
import PageHeader from '@/components/layout/PageHeader.vue';
import SeasonSelect from '@/components/filters/SeasonSelect.vue';
import CompetitionStageSelect from '@/components/filters/CompetitionStageSelect.vue';
import { getCompetitionStageGroups } from '@/utils/skillQualityConfig';

const props = defineProps({
    bestPlayers: {
        type: Object,
        default: () => ({
            attack: { top: null, list: [] },
            serve: { top: null, list: [] },
            block: { top: null, list: [] },
            reception: { top: null, list: [] },
        })
    },
    availableSeasons: {
        type: Array,
        default: () => []
    },
    currentSeasonId: {
        type: Number,
        default: null
    },
    positions: {
        type: Array,
        default: () => []
    },
    title: {
        type: String,
        default: 'Топ игроков',
    },
    headingTag: {
        type: String,
        default: 'h1',
        validator: (value) => ['h1', 'h2'].includes(value),
    },
});

const categories = [
    {
        key: 'attack',
        title: 'Атака',
        sortOptions: [
            { value: 'kills', label: 'Очки' },
            { value: 'kills_per_set', label: 'Очков за партию' },
            { value: 'efficiency', label: 'Эффективность' }
        ]
    },
    {
        key: 'serve',
        title: 'Подача',
        sortOptions: [
            { value: 'aces', label: 'Очки' },
            { value: 'aces_per_set', label: 'Очков за партию' },
            { value: 'efficiency', label: 'Эффективность' }
        ]
    },
    {
        key: 'block',
        title: 'Блок',
        sortOptions: [
            { value: 'kills', label: 'Очки' },
            { value: 'kills_per_set', label: 'Очков за партию' },
            { value: 'efficiency', label: 'Эффективность' }
        ]
    },
    {
        key: 'reception',
        title: 'Прием',
        sortOptions: [
            { value: 'perfect', label: 'Отличный, %' },
            { value: 'positive', label: 'Позитивный, %' },
            { value: 'efficiency', label: 'Эффективность' }
        ]
    }
];

function getDefaultCompetitionSelection() {
    const groups = getCompetitionStageGroups();

    return {
        competitionTypes: groups.map((group) => group.value),
        stages: groups.flatMap((group) => group.stages.map((stage) => stage.value)),
    };
}

const defaults = getDefaultCompetitionSelection();

const bestPlayersData = ref(props.bestPlayers);
const loading = ref(false);
const selectedSeasonId = ref(props.currentSeasonId);
const competitionTypes = ref([...defaults.competitionTypes]);
const stages = ref([...defaults.stages]);

const sortParams = ref({
    attack: 'kills',
    serve: 'aces',
    block: 'kills',
    reception: 'positive'
});

const selectedPositions = ref({});

const competitionsParam = computed(() => buildCompetitionsParam(competitionTypes.value, stages.value));

function buildCompetitionsParam(types, selectedStages) {
    const result = [];
    const groups = getCompetitionStageGroups();

    groups.forEach((group) => {
        if (!types.includes(group.value)) {
            return;
        }

        if (!group.stages.length) {
            result.push(group.value);
            return;
        }

        group.stages.forEach((stage) => {
            if (selectedStages.includes(stage.value)) {
                result.push(stage.value);
            }
        });
    });

    return result;
}

onMounted(() => {
    categories.forEach((category) => {
        if (selectedPositions.value[category.key]) {
            return;
        }

        const categoriesWithoutLibero = ['attack', 'serve', 'block'];
        if (categoriesWithoutLibero.includes(category.key)) {
            selectedPositions.value[category.key] = props.positions
                .filter((p) => p.slug !== 'libero')
                .map((p) => p.slug);
        } else if (category.key === 'reception') {
            selectedPositions.value[category.key] = props.positions
                .filter((p) => p.slug === 'outside_hitter' || p.slug === 'libero')
                .map((p) => p.slug);
        } else {
            selectedPositions.value[category.key] = props.positions.map((p) => p.slug);
        }
    });
});

watch(() => props.currentSeasonId, (newSeasonId) => {
    if (newSeasonId !== null) {
        selectedSeasonId.value = newSeasonId;
    }
});

function onSeasonChange(seasonId) {
    selectedSeasonId.value = seasonId;
    if (seasonId !== null) {
        loadBestPlayers(seasonId);
    }
}

function onCompetitionChange({ competitionTypes: nextTypes, stages: nextStages }) {
    competitionTypes.value = nextTypes;
    stages.value = nextStages;
    if (selectedSeasonId.value !== null) {
        loadBestPlayers(selectedSeasonId.value);
    }
}

function onSortChange(categoryKey, sortBy) {
    sortParams.value[categoryKey] = sortBy;
}

function onPositionsChange(categoryKey, positions) {
    selectedPositions.value[categoryKey] = positions;
}

function onCategoryDataUpdated(categoryKey, data) {
    bestPlayersData.value[categoryKey] = data;
}

async function loadBestPlayers(seasonId) {
    if (seasonId === null) {
        return;
    }

    loading.value = true;
    try {
        const params = {
            season_id: seasonId,
            attack_sort: sortParams.value.attack,
            serve_sort: sortParams.value.serve,
            block_sort: sortParams.value.block,
            reception_sort: sortParams.value.reception,
        };

        categories.forEach((category) => {
            const positions = selectedPositions.value[category.key] || props.positions.map((p) => p.slug);
            if (positions && positions.length > 0) {
                params[`${category.key}_positions`] = positions;
            }
        });

        if (competitionsParam.value.length > 0) {
            params.competitions = competitionsParam.value;
        }

        const response = await axios.get('/api/best-players', { params });
        bestPlayersData.value = response.data;
    } catch (error) {
        console.error('Error loading best players:', error);
    } finally {
        loading.value = false;
    }
}
</script>

<style lang="scss" scoped>
@import '../../../scss/tokens';

.best-players-section {
    margin-bottom: $space-10;

    .best-players-grid {
        display: grid;
        grid-template-columns: minmax(0, 1fr);
        gap: $space-4;

        @include up($bp-md) {
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: $space-5;
        }
    }
}
</style>
